import { defineStore } from "pinia";
import type { RouteLocationNormalized, RouteRecordRaw, Router } from "vue-router";
import { getUserPreferenceDetail, saveUserPreference } from "@shared/api/userPreference";
import { isRouteNoPin } from "@shared/router/routePageExtras";
import { LEGACY_MENU_PATH_REDIRECTS } from "@shared/router/mapUserMenus";
import { isNavigableAppPath } from "@shared/utils/tagNavigation";

export interface TagView {
    path: string;
    fullPath: string;
    name: string;
    title: string;
    affix?: boolean;
    noCache?: boolean;
    query?: Record<string, any>;
}

/** 递归扫描路由表，挑出所有 meta.affix = true 的项 */
function filterAffixRoutes(routes: RouteRecordRaw[], basePath = "/"): TagView[] {
    const tags: TagView[] = [];
    for (const route of routes) {
        const fullPath = (basePath + "/" + route.path).replace(/\/+/g, "/");
        if (route.meta?.affix && route.name) {
            tags.push({
                path: fullPath,
                fullPath,
                name: route.name as string,
                title: (route.meta?.title as string) || (route.name as string),
                affix: true,
            });
        }
        if (route.children?.length) {
            tags.push(...filterAffixRoutes(route.children, fullPath));
        }
    }
    return tags;
}

interface TagsViewState {
    /** 已访问的页签列表 */
    visitedViews: TagView[];
    /** 用户固定的页签 */
    fixedViews: TagView[];
    /** 是否已从偏好接口拉取过 */
    hydrated: boolean;
}

/** 通用偏好 key：已打开页签 + 固定页签 */
const TAGS_PREFERENCE_KEY = "erp/layout/tags-view";
/** 历史本地 key（未固定页签曾落本地），升级后迁移并清理 */
const LEGACY_VISITED_KEY = "erp_visited_views";
const LEGACY_FIXED_KEY = "erp_fixed_tags";

interface TagsPreferenceContent {
    visitedViews?: TagView[];
    fixedViews?: TagView[];
}

function cloneTag(tag: TagView): TagView {
    return {
        path: tag.path,
        fullPath: tag.fullPath,
        name: tag.name,
        title: tag.title,
        affix: tag.affix,
        noCache: tag.noCache,
        query: tag.query ? { ...tag.query } : undefined,
    };
}

function rewriteLegacyMenuPath(path: string): string {
    return LEGACY_MENU_PATH_REDIRECTS[path] || path;
}

function rewriteLegacyTag(tag: TagView): TagView {
    const nextPath = rewriteLegacyMenuPath(tag.path);
    if (nextPath === tag.path) return tag;
    const full = tag.fullPath || tag.path;
    const nextFull = full === tag.path || full.startsWith(`${tag.path}?`) || full.startsWith(`${tag.path}/`) ? `${nextPath}${full.slice(tag.path.length)}` : nextPath;
    return { ...tag, path: nextPath, fullPath: nextFull };
}

function normalizeTagList(raw: unknown): TagView[] {
    if (!Array.isArray(raw)) return [];
    const result: TagView[] = [];
    const seen = new Set<string>();
    for (const item of raw) {
        if (!item || typeof item !== "object") continue;
        const tag = item as Partial<TagView>;
        if (!tag.path) continue;
        const normalized = rewriteLegacyTag({
            path: String(tag.path),
            fullPath: String(tag.fullPath || tag.path),
            name: String(tag.name || ""),
            title: String(tag.title || tag.name || tag.path),
            affix: !!tag.affix,
            noCache: !!tag.noCache,
            query: tag.query && typeof tag.query === "object" ? { ...tag.query } : undefined,
        });
        const key = normalized.fullPath || normalized.path;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(normalized);
    }
    return result;
}

function parsePreferenceContent(content?: string | null): TagsPreferenceContent {
    if (!content) return {};
    try {
        const parsed = JSON.parse(content) as TagsPreferenceContent | TagView[];
        // 兼容误存成数组的情况
        if (Array.isArray(parsed)) {
            return { visitedViews: normalizeTagList(parsed), fixedViews: [] };
        }
        return {
            visitedViews: normalizeTagList(parsed?.visitedViews),
            fixedViews: normalizeTagList(parsed?.fixedViews),
        };
    } catch {
        return {};
    }
}

function readLegacyLocalVisited(): TagView[] {
    try {
        const raw = localStorage.getItem(LEGACY_VISITED_KEY);
        if (!raw) return [];
        return normalizeTagList(JSON.parse(raw)).map(tag => ({ ...tag, affix: false }));
    } catch {
        return [];
    }
}

function clearLegacyLocalCache() {
    localStorage.removeItem(LEGACY_VISITED_KEY);
    localStorage.removeItem(LEGACY_FIXED_KEY);
}

function upsertVisited(list: TagView[], tag: TagView): void {
    const idx = list.findIndex(v => v.path === tag.path);
    if (idx === -1) {
        list.push(cloneTag(tag));
        return;
    }
    const prev = list[idx];
    list[idx] = {
        ...prev,
        ...cloneTag(tag),
        name: tag.name || prev.name,
        title: tag.title || prev.title,
        affix: tag.affix || prev.affix,
    };
}

function tagsEqual(a: TagView, b: TagView): boolean {
    return (
        a.path === b.path &&
        a.fullPath === b.fullPath &&
        a.name === b.name &&
        a.title === b.title &&
        !!a.affix === !!b.affix &&
        !!a.noCache === !!b.noCache &&
        JSON.stringify(a.query || {}) === JSON.stringify(b.query || {})
    );
}

/** 分区顺序：系统 affix → 用户固定（按 fixedViews）→ 未固定 */
function sortVisitedViews(visited: TagView[], fixed: TagView[]): TagView[] {
    const fixedPathSet = new Set(fixed.map(f => f.path));
    const byPath = new Map(visited.map(v => [v.path, v]));
    const affix = visited.filter(v => v.affix);
    const pinned: TagView[] = [];
    for (const f of fixed) {
        const tag = byPath.get(f.path);
        if (tag && !tag.affix) pinned.push(tag);
    }
    // 兜底：visited 里标了固定但 fixedViews 漏了的
    for (const v of visited) {
        if (!v.affix && fixedPathSet.has(v.path) && !pinned.some(p => p.path === v.path)) {
            pinned.push(v);
        }
    }
    const pinnedPaths = new Set(pinned.map(p => p.path));
    const normal = visited.filter(v => !v.affix && !pinnedPaths.has(v.path));
    return [...affix, ...pinned, ...normal];
}

/** 拖拽分区：用户固定 / 未固定（affix 单独置顶，不参与跨区） */
export function getTagDragZone(tag: TagView, isUserFixed: boolean): "fixed" | "normal" {
    if (tag.affix || isUserFixed) return "fixed";
    return "normal";
}

function buildPreferenceContent(visitedViews: TagView[], fixedViews: TagView[]): string {
    return JSON.stringify({
        visitedViews: visitedViews.map(cloneTag),
        fixedViews: fixedViews.map(cloneTag),
    } satisfies TagsPreferenceContent);
}

let persistTimer: ReturnType<typeof setTimeout> | null = null;
let persistQueued = false;
let hydratePromise: Promise<void> | null = null;
/** 上次成功写入 / 拉取后的 content，用于跳过无变化 save */
let lastPersistedContent = "";

export const useTagsViewStore = defineStore("tagsView", {
    state: (): TagsViewState => ({
        visitedViews: [],
        fixedViews: [],
        hydrated: false,
    }),

    getters: {
        /** keep-alive 缓存的组件 name 列表 */
        cachedNames(state): string[] {
            return state.visitedViews.filter(v => !!v.name && !v.noCache).map(v => v.name);
        },
    },

    actions: {
        /** 防抖写入通用偏好；内容未变则跳过 */
        persist(immediate = false) {
            if (!this.hydrated) return;
            persistQueued = true;
            const flush = () => {
                if (!persistQueued) return;
                persistQueued = false;
                const content = buildPreferenceContent(this.visitedViews, this.fixedViews);
                if (content === lastPersistedContent) return;
                lastPersistedContent = content;
                void saveUserPreference({ key: TAGS_PREFERENCE_KEY, content }).catch(() => {
                    lastPersistedContent = "";
                });
            };
            if (immediate) {
                if (persistTimer) {
                    clearTimeout(persistTimer);
                    persistTimer = null;
                }
                flush();
                return;
            }
            if (persistTimer) clearTimeout(persistTimer);
            persistTimer = setTimeout(() => {
                persistTimer = null;
                flush();
            }, 300);
        },

        /** 从 userPreference 拉取全部已打开页签 / 固定状态 */
        async hydrateFromPreference() {
            if (this.hydrated) return;
            if (hydratePromise) {
                await hydratePromise;
                return;
            }
            hydratePromise = (async () => {
                const legacyVisited = readLegacyLocalVisited();
                clearLegacyLocalCache();
                try {
                    const data = await getUserPreferenceDetail(TAGS_PREFERENCE_KEY);
                    const parsed = parsePreferenceContent(data?.content);
                    this.fixedViews = (parsed.fixedViews ?? []).map(tag => ({ ...tag, affix: false }));
                    // 接口有 visited 用接口；仅固定数据时用本机历史未固定页签做一次迁移
                    const fromApi = parsed.visitedViews ?? [];
                    if (fromApi.length) {
                        this.visitedViews = fromApi;
                    } else if (legacyVisited.length) {
                        this.visitedViews = legacyVisited;
                    } else {
                        this.visitedViews = [];
                    }
                    lastPersistedContent = data?.content?.trim() ? buildPreferenceContent(this.visitedViews, this.fixedViews) : "";
                    // 本地有未固定、接口尚无 visited 时，hydrate 后由 init/persist 写回接口
                    if (!fromApi.length && legacyVisited.length) {
                        lastPersistedContent = "";
                    }
                } catch {
                    this.visitedViews = legacyVisited;
                    this.fixedViews = [];
                    lastPersistedContent = "";
                } finally {
                    this.hydrated = true;
                    hydratePromise = null;
                }
            })();
            await hydratePromise;
        },

        /**
         * 启动 / 权限就绪后：
         * 1) 注入 meta.affix
         * 2) 把用户固定页签补进 visitedViews
         */
        initAffixViews(routes: RouteRecordRaw[]) {
            const affixViews = filterAffixRoutes(routes);
            for (const tag of affixViews) {
                if (!this.visitedViews.find(v => v.path === tag.path)) {
                    this.visitedViews.unshift(cloneTag(tag));
                } else {
                    const exist = this.visitedViews.find(v => v.path === tag.path)!;
                    exist.affix = true;
                    exist.name = tag.name || exist.name;
                    exist.title = tag.title || exist.title;
                }
            }

            this.fixedViews = this.fixedViews.filter(v => !isRouteNoPin(v.name));

            for (const fixed of this.fixedViews) {
                if (fixed.affix) continue;
                if (!fixed.name && !this.visitedViews.find(v => v.path === fixed.path)) continue;
                upsertVisited(this.visitedViews, { ...fixed, affix: false });
            }

            this.normalizeVisitedOrder();
            this.persist();
        },

        /** 保证 visitedViews 分区顺序，并同步 fixedViews 顺序 */
        normalizeVisitedOrder() {
            this.visitedViews = sortVisitedViews(this.visitedViews, this.fixedViews);
            const nextFixed: TagView[] = [];
            for (const v of this.visitedViews) {
                if (v.affix) continue;
                const exist = this.fixedViews.find(f => f.path === v.path);
                if (exist) nextFixed.push(cloneTag({ ...exist, ...v, affix: false }));
            }
            this.fixedViews = nextFixed;
        },

        /**
         * 按 DOM 顺序重排（拖拽结束）。
         * 仅允许固定区内部 / 未固定区内部重排；跨区则回归一化顺序。
         */
        reorderVisitedByPaths(paths: string[]) {
            if (!paths.length) return;
            const byPath = new Map(this.visitedViews.map(v => [v.path, v]));
            if (paths.length !== this.visitedViews.length || paths.some(p => !byPath.has(p))) {
                this.normalizeVisitedOrder();
                this.persist();
                return;
            }

            const next = paths.map(p => byPath.get(p)!);
            const isUserFixed = (tag: TagView) => this.fixedViews.some(f => f.path === tag.path);
            const zoneOf = (tag: TagView) => getTagDragZone(tag, isUserFixed(tag));

            const nextAffix = next.filter(v => v.affix);
            const nextFixed = next.filter(v => !v.affix && zoneOf(v) === "fixed");
            const nextNormal = next.filter(v => zoneOf(v) === "normal");
            const partitioned = [...nextAffix, ...nextFixed, ...nextNormal];
            // 必须保持「affix | 固定 | 未固定」三段连续，禁止跨区
            if (partitioned.some((tag, idx) => tag.path !== next[idx].path)) {
                this.normalizeVisitedOrder();
                this.persist();
                return;
            }

            const prevAffix = this.visitedViews.filter(v => v.affix).map(v => v.path);
            const prevFixed = this.visitedViews.filter(v => !v.affix && isUserFixed(v)).map(v => v.path);
            const prevNormal = this.visitedViews.filter(v => !v.affix && !isUserFixed(v)).map(v => v.path);
            const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every(p => b.includes(p));
            if (
                !sameSet(
                    prevAffix,
                    nextAffix.map(v => v.path),
                ) ||
                !sameSet(
                    prevFixed,
                    nextFixed.map(v => v.path),
                ) ||
                !sameSet(
                    prevNormal,
                    nextNormal.map(v => v.path),
                )
            ) {
                this.normalizeVisitedOrder();
                this.persist();
                return;
            }

            // 先按拖拽后的 DOM 顺序写回 fixedViews，再 normalize；
            // 否则 sortVisitedViews 仍按旧 fixedViews 排固定区，顺序丢且偏好存不到。
            this.visitedViews = next;
            this.fixedViews = nextFixed.map(v => {
                const exist = this.fixedViews.find(f => f.path === v.path);
                return cloneTag({ ...(exist || v), ...v, affix: false });
            });
            this.normalizeVisitedOrder();
            this.persist(true);
        },

        addView(route: RouteLocationNormalized) {
            if (!route.name) return;
            // 与 installGuards 一致：只看叶子自身 hidden，忽略 Layout 父级合并
            const leaf = route.matched?.[route.matched.length - 1];
            if (leaf?.meta?.hidden) return;

            const tagPath = (route.meta?.singletonTagPath as string) || route.path;
            /** keep-alive 按组件 name 匹配；添加/编辑共用组件时用 keepAliveName */
            const cacheName = (route.meta?.keepAliveName as string) || (route.name as string);
            const exist = this.visitedViews.find(v => v.path === tagPath);
            if (exist) {
                const next: TagView = {
                    ...exist,
                    fullPath: route.fullPath,
                    query: route.query,
                    title: (route.meta?.title as string) || exist.title,
                    name: cacheName,
                    noCache: !!route.meta?.noCache,
                };
                const fixed = this.fixedViews.find(v => v.path === tagPath);
                const fixedChanged =
                    !!fixed && (fixed.fullPath !== next.fullPath || fixed.name !== next.name || fixed.title !== next.title || JSON.stringify(fixed.query || {}) !== JSON.stringify(next.query || {}));
                if (tagsEqual(exist, next) && !fixedChanged) return;
                Object.assign(exist, next);
                if (fixed) {
                    fixed.fullPath = next.fullPath;
                    fixed.query = next.query;
                    fixed.title = next.title;
                    fixed.name = next.name;
                }
                this.persist();
                return;
            }

            const tag: TagView = {
                path: tagPath,
                fullPath: route.fullPath,
                name: cacheName,
                title: (route.meta?.title as string) || "no-name",
                affix: !!route.meta?.affix,
                noCache: !!route.meta?.noCache,
                query: route.query,
            };

            if (tag.affix) {
                const insertAt = this.visitedViews.findIndex(v => !v.affix);
                if (insertAt === -1) {
                    this.visitedViews.push(tag);
                } else {
                    this.visitedViews.splice(insertAt, 0, tag);
                }
            } else {
                this.visitedViews.push(tag);
            }
            this.persist();
        },

        delView(view: TagView): TagView | undefined {
            if (view.affix || this.isFixed(view)) return undefined;
            const idx = this.visitedViews.findIndex(v => v.path === view.path);
            if (idx === -1) return undefined;
            const removed = this.visitedViews.splice(idx, 1)[0];
            this.persist();
            return this.visitedViews[idx] || this.visitedViews[idx - 1] || removed;
        },

        delOthersViews(view: TagView) {
            this.visitedViews = this.visitedViews.filter(v => v.affix || this.isFixed(v) || v.path === view.path);
            this.persist();
        },

        delAllViews() {
            this.visitedViews = this.visitedViews.filter(v => v.affix || this.isFixed(v));
            this.persist();
        },

        /** 固定页签（移入固定区末尾） */
        addFixed(view: TagView) {
            if (view.affix || isRouteNoPin(view.name)) return;
            if (this.fixedViews.some(v => v.path === view.path)) return;
            this.fixedViews.push(cloneTag({ ...view, affix: false }));
            upsertVisited(this.visitedViews, { ...view, affix: false });
            this.normalizeVisitedOrder();
            this.persist(true);
        },

        /** 取消固定（移入未固定区末尾） */
        removeFixed(view: TagView) {
            this.fixedViews = this.fixedViews.filter(v => v.path !== view.path);
            this.normalizeVisitedOrder();
            this.persist(true);
        },

        isFixed(view: TagView): boolean {
            return this.fixedViews.some(v => v.path === view.path);
        },

        /** 去掉指定 path 的页签（含用户固定；系统 affix 保留） */
        dropPath(path: string) {
            if (!path) return;
            const keep = (tag: TagView) => tag.affix || (tag.path !== path && tag.fullPath !== path);
            const nextVisited = this.visitedViews.filter(keep);
            const nextFixed = this.fixedViews.filter(tag => tag.path !== path && tag.fullPath !== path);
            if (nextVisited.length === this.visitedViews.length && nextFixed.length === this.fixedViews.length) return;
            this.visitedViews = nextVisited;
            this.fixedViews = nextFixed;
            this.normalizeVisitedOrder();
            this.persist();
        },

        /** 权限就绪后：清掉已无路由 / 无权限的页签 */
        pruneInaccessibleViews(router: Router | null) {
            if (!router) return;
            const keep = (tag: TagView) => tag.affix || isNavigableAppPath(tag.path, router);
            const nextVisited = this.visitedViews.filter(keep);
            const nextFixed = this.fixedViews.filter(keep);
            if (nextVisited.length === this.visitedViews.length && nextFixed.length === this.fixedViews.length) return;
            this.visitedViews = nextVisited;
            this.fixedViews = nextFixed;
            this.normalizeVisitedOrder();
            this.persist();
        },

        /** 登出：只清内存（服务端偏好按账号保留） */
        resetTags() {
            if (persistTimer) {
                clearTimeout(persistTimer);
                persistTimer = null;
            }
            persistQueued = false;
            hydratePromise = null;
            lastPersistedContent = "";
            this.visitedViews = [];
            this.fixedViews = [];
            this.hydrated = false;
            clearLegacyLocalCache();
        },

        /** 用于刷新当前页签 */
        delCachedView(view: TagView) {
            void view;
        },
    },
});
